import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SmsIcon from "@mui/icons-material/Sms";
import EmailIcon from "@mui/icons-material/Email";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Header from "../Includes/Header";
import ListPagination from "../../components/ListPagination/ListPagination";
import { useTheme as useAppTheme } from "../../contexts/ThemeContext";
import { getNotificationSummary, getNotificationLogs } from "../Apihelper/NotificationLogApi";

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: <WhatsAppIcon />, color: "#25D366", toCol: "Number", subjectCol: "Template" },
  { key: "sms", label: "SMS", icon: <SmsIcon />, color: "#2563eb", toCol: "Number", subjectCol: "Template ID" },
  { key: "email", label: "Email", icon: <EmailIcon />, color: "#f59e0b", toCol: "Email", subjectCol: "Subject" },
];
const PERIODS = [
  { key: "today", label: "Today" },
  { key: "last_7d", label: "7 days" },
  { key: "last_30d", label: "30 days" },
  { key: "all_time", label: "All time" },
];

const getToken = () => {
  try { return JSON.parse(localStorage.getItem("userdata") || "{}").token; } catch (e) { return null; }
};

/** A readable line for the Detail column instead of the provider's raw reply. */
const describe = (row) => {
  if (row.status === "failed") {
    const err = row.error || "";
    if (/Not Found \(404\)/i.test(err)) return "Template not found at the provider (404)";
    return err || "Failed";
  }
  if (row.channel === "whatsapp") {
    try {
      const j = JSON.parse(row.response || "{}");
      const st = j?.ApiMessage?.messages?.[0]?.message_status;
      return st ? `Accepted by WhatsApp (${st})` : "Accepted by WhatsApp";
    } catch (e) { return "Accepted by WhatsApp"; }
  }
  if (row.channel === "email") return "Handed to the mail server";
  return "Sent to the SMS provider";
};

function StatusBadge({ status }) {
  const ok = status === "sent";
  return (
    <Chip
      size="small"
      icon={ok ? <CheckCircleIcon /> : <ErrorIcon />}
      label={ok ? "Sent" : "Failed"}
      color={ok ? "success" : "error"}
      sx={{ fontWeight: 600 }}
    />
  );
}

/** One channel: a clickable header with its counts, and its own table that opens and closes. */
function ChannelSection({ channel, summary, filters, refreshKey, isMobile }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const filterKey = `${filters.status}|${filters.search}|${filters.date_from}|${filters.date_to}`;
  useEffect(() => { setPage(0); }, [filterKey]);

  const load = useCallback(async () => {
    const token = getToken();
    if (!token) { navigate("/"); return; }
    setLoading(true);
    try {
      const res = await getNotificationLogs(token, {
        channel: channel.key,
        status: filters.status || undefined,
        search: filters.search || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        limit: pageSize,
        index: page * pageSize,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setTotal(Number(res?.total_count) || 0);
    } catch (e) {
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [channel.key, filterKey, page, pageSize, navigate]);

  useEffect(() => { if (open) load(); }, [open, load, refreshKey]);

  const get = (p) => summary?.[channel.key]?.[p] || { sent: 0, failed: 0 };
  const all = get("all_time");
  const attempts = all.sent + all.failed;
  const rate = attempts ? Math.round((all.sent / attempts) * 100) : null;

  return (
    <Paper elevation={0} sx={{ mb: 2, borderRadius: 3, border: "1px solid", borderColor: open ? channel.color : "divider", overflow: "hidden", transition: "border-color .2s" }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        role="button"
        aria-expanded={open}
        sx={{ p: { xs: 1.5, sm: 2 }, cursor: "pointer", background: `linear-gradient(135deg, ${channel.color}14 0%, transparent 65%)`, "&:hover": { bgcolor: `${channel.color}0d` } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 42, height: 42, borderRadius: 2.5, bgcolor: `${channel.color}22`, color: channel.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {channel.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={700}>{channel.label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {rate === null ? "No messages yet" : `${rate}% delivered overall · tap to ${open ? "hide" : "see"} the list`}
            </Typography>
          </Box>
          <ExpandMoreIcon sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", color: "text.secondary" }} />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: 1, mt: 1.5 }}>
          {PERIODS.map((p) => {
            const v = get(p.key);
            return (
              <Box key={p.key} sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, px: 1.25, py: 0.75 }}>
                <Typography variant="caption" color="text.secondary">{p.label}</Typography>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                  <Typography fontWeight={800} sx={{ fontSize: 20, color: channel.color, lineHeight: 1.2 }}>{v.sent}</Typography>
                  <Typography variant="caption" color="text.secondary">sent</Typography>
                  {v.failed > 0 && <Typography variant="caption" sx={{ color: "error.main", fontWeight: 700, ml: "auto" }}>{v.failed} failed</Typography>}
                </Box>
              </Box>
            );
          })}
        </Box>
        {rate !== null && (
          <LinearProgress variant="determinate" value={rate} sx={{ height: 5, borderRadius: 3, mt: 1.5, bgcolor: "action.hover", "& .MuiLinearProgress-bar": { bgcolor: channel.color, borderRadius: 3 } }} />
        )}
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress size={26} /></Box>
          ) : rows.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography fontWeight={600}>No {channel.label} messages in this range</Typography>
              <Typography variant="body2" color="text.secondary">Change the dates or filters above.</Typography>
            </Box>
          ) : isMobile ? (
            <Box sx={{ display: "grid", gap: 1, p: 1.5 }}>
              {rows.map((r) => (
                <Paper key={r.id} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid", borderColor: "divider", borderLeft: `4px solid ${r.status === "sent" ? "#16a34a" : "#dc2626"}` }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography fontWeight={700} sx={{ flex: 1, minWidth: 0 }} noWrap>{r.template || channel.label}</Typography>
                    <StatusBadge status={r.status} />
                  </Box>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{r.recipient || "-"}</Typography>
                  <Typography variant="caption" color="text.secondary">{dayjs(r.sent_at).format("DD-MM-YYYY HH:mm")}</Typography>
                  <Typography variant="caption" sx={{ display: "block", mt: 0.25, color: r.status === "failed" ? "error.main" : "text.secondary" }}>{describe(r)}</Typography>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { bgcolor: "#1e293b", color: "#fff", fontWeight: 600 } }}>
                    <TableCell>Time</TableCell>
                    <TableCell>{channel.subjectCol}</TableCell>
                    <TableCell>{channel.toCol}</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{dayjs(r.sent_at).format("DD-MM-YYYY HH:mm")}</TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>{r.template || "-"}</TableCell>
                      <TableCell>{r.recipient || "-"}</TableCell>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                      <TableCell sx={{ color: r.status === "failed" ? "error.main" : "text.primary" }}>
                        <Tooltip title={r.response || r.error || ""}><span>{describe(r)}</span></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <ListPagination
            count={total}
            page={page}
            rowsPerPage={pageSize}
            onPageChange={setPage}
            onRowsPerPageChange={(n) => { setPageSize(n); setPage(0); }}
          />
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function NotificationLogsPage() {
  const { colors: appColors } = useAppTheme();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filters, setFilters] = useState({
    status: "", search: "",
    date_from: dayjs().subtract(6, "day").format("YYYY-MM-DD"),
    date_to: dayjs().format("YYYY-MM-DD"),
  });

  const loadSummary = useCallback(async () => {
    const token = getToken();
    if (!token) { navigate("/"); return; }
    try {
      const res = await getNotificationSummary(token);
      if (res?.data) setSummary(res.data);
    } catch (e) { /* summary is optional */ }
  }, [navigate]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const setFilter = (name, value) => setFilters((f) => ({ ...f, [name]: value }));
  const refresh = () => { loadSummary(); setRefreshKey((k) => k + 1); };

  const StatusPill = ({ value, label, color }) => (
    <Chip
      label={label}
      onClick={() => setFilter("status", value)}
      sx={{
        fontWeight: 600,
        bgcolor: filters.status === value ? (color || appColors.evergreen) : "transparent",
        color: filters.status === value ? "#fff" : "text.secondary",
        border: "1px solid",
        borderColor: filters.status === value ? "transparent" : "divider",
        "&:hover": { bgcolor: filters.status === value ? (color || appColors.evergreen) : "action.hover" },
      }}
    />
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <CssBaseline />
      <Header />
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Box sx={{ mt: { xs: "72px", sm: "80px" }, maxWidth: 1400, mx: "auto" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2.5 }}>
            <Box>
              <Typography variant="h5" sx={{ color: appColors.evergreen, fontWeight: 700 }}>Notification Log</Typography>
              <Typography variant="body2" color="text.secondary">
                Every WhatsApp, SMS and email the app sent or failed to send. Tap a channel to open its list.
              </Typography>
            </Box>
            <Tooltip title="Refresh">
              <IconButton onClick={refresh} sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, mb: 2, border: "1px solid", borderColor: "divider" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
              <StatusPill value="" label="Any status" />
              <StatusPill value="sent" label="Sent" color="#16a34a" />
              <StatusPill value="failed" label="Failed" color="#dc2626" />
            </Box>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.date_from} onChange={(e) => setFilter("date_from", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.date_to} onChange={(e) => setFilter("date_to", e.target.value)} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth size="small" label="Search number, email or template" value={filters.search} onChange={(e) => setFilter("search", e.target.value)} />
              </Grid>
            </Grid>
          </Paper>

          {CHANNELS.map((c) => (
            <ChannelSection key={c.key} channel={c} summary={summary} filters={filters} refreshKey={refreshKey} isMobile={isMobile} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
